import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useNotificationsSummary,
  useNotificationsList,
  useMarkNotificationRead,
} from '@api/queries';
import { notifTargetRoute } from '@auth/notifRoute';
import styles from './Header.module.css';

/**
 * Standalone notification bell — used by every role shell (Super Admin,
 * Doctor, Staff). The backend already scopes notifications to the caller's
 * clinic, so the same component surfaces the right feed for whoever is logged
 * in. `seeAllPath` is where "See all" navigates for this shell.
 */
export default function NotificationBell({ seeAllPath = '/notifications' }) {
  const navigate = useNavigate();
  const { data: notifSummary } = useNotificationsSummary();
  const notificationCount = notifSummary?.unread ?? 0;

  const [open, setOpen] = useState(false);
  // Only load the list once the dropdown is opened — the badge count comes
  // from the lightweight summary, so the list is dead weight until then.
  const { data: notifPages } = useNotificationsList({}, { enabled: open });
  const recent = (notifPages?.pages?.[0]?.notifications || []).slice(0, 5);
  const markRead = useMarkNotificationRead();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onItem = (n) => {
    setOpen(false);
    if (!n.readAt) markRead.mutate(n.id);
    // Role-aware target so the bell never bounces the viewer into another app's
    // route (QA NOT-001). null → stay put.
    const target = notifTargetRoute(n);
    if (target) navigate(target);
  };

  return (
    <div className={styles.notifWrap}>
      <button
        type="button"
        className={`${styles.bell} ${open ? styles.bellActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
      >
        <i className="bi bi-bell" aria-hidden="true" />
        {notificationCount > 0 && (
          <span className={styles.bellBadge}>{notificationCount > 99 ? '99+' : notificationCount}</span>
        )}
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.notifDropdown}>
            <div className={styles.notifDropdownHead}>
              <div className={styles.notifDropdownTitle}>Notifications</div>
              {notificationCount > 0 && (
                <div className={styles.notifDropdownCount}>{notificationCount} unread</div>
              )}
            </div>

            <div className={styles.notifDropdownList}>
              {recent.length === 0 ? (
                <div className={styles.notifDropdownEmpty}>
                  <div className={styles.notifDropdownEmptyIcon}><i className="bi bi-bell-slash" aria-hidden="true" /></div>
                  <div>No notifications yet</div>
                </div>
              ) : (
                recent.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`${styles.notifDropdownItem} ${!n.readAt ? styles.notifDropdownItemUnread : ''}`}
                    onClick={() => onItem(n)}
                  >
                    <span className={styles.notifDropdownItemIcon}><i className={`bi bi-${iconForKind(n.kind)}`} aria-hidden="true" /></span>
                    <div className={styles.notifDropdownItemMain}>
                      <div className={styles.notifDropdownItemTitle}>
                        {n.title}
                        {n.severity === 'critical' && <span className={styles.notifDropdownItemCriticalDot} />}
                      </div>
                      <div className={styles.notifDropdownItemMeta}>
                        {n.clinic || 'Network'} · {formatRelative(n.createdAt)}
                      </div>
                    </div>
                    {!n.readAt && <span className={styles.notifDropdownItemUnreadDot} />}
                  </button>
                ))
              )}
            </div>

            <button
              type="button"
              className={styles.notifDropdownFooter}
              onClick={() => { setOpen(false); navigate(seeAllPath); }}
            >
              See all notifications <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function iconForKind(kind) {
  switch (kind) {
    case 'compliance':       return 'calendar-check';
    case 'data-correction':  return 'tools';
    case 'inventory':        return 'capsule';
    case 'reminder':         return 'clock';
    case 'booking':          return 'calendar-check';
    case 'system':           return 'gear';
    case 'clinical':         return 'heart-pulse';
    default:                 return 'megaphone';
  }
}

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const min = Math.floor((Date.now() - then) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
